import { DEFAULT_PLOT_DECIMATED_FPS, PLOT_WINDOW_SIZE } from '../../utils/constants/constants';
import { RecordValueWithSensograms, SENSOR_NATURE } from '../../services/cache/idb';
import { parseBiosensorsSignalEvent, parseEventPayload, parseResponsePayload } from '../serial/csm';
import { SignatureWithSpotgrid, CSMMisoMessage } from '../../types/types';
import { agg, mean } from './utils';
import { AggregationMethod, CustomMinMaxNormalisationRules, LinearFitResult } from './definitions';
import { createEmpty2dArray } from '../../utils/helpers/array';

export const normalizeL2 = (a: number[]): number[] => {
  let d = a.map((cur) => Math.pow(cur, 2));
  let dd = d.reduce((acc, cur) => (acc += cur), 0);
  let ddd = Math.sqrt(dd);
  return a.map((cur) => cur / ddd);
};

export const customMinMaxNormalisation = (rawSignal: number[], seriesLabels: number[], normRules: CustomMinMaxNormalisationRules): number[] => {
  // 1. Check that rawSignal is the same size as seriesLabels
  if (rawSignal.length !== seriesLabels.length) {
    throw new Error(`Raw signal length (${rawSignal.length}) does not match series labels length (${seriesLabels.length})`);
  }

  // 2. Get relveant positions for normalisation
  let minSpot = normRules.minSpot;
  let maxSpot = normRules.maxSpot;

  const minSpotIndex = seriesLabels.indexOf(minSpot);
  const maxSpotIndex = seriesLabels.indexOf(maxSpot);

  // Validate that we found both indices
  if (minSpotIndex === -1 || maxSpotIndex === -1) {
    throw new Error('Could not find expected spots for minmax normalisation in spotgrid');
  }

  // 3. Compute normalized values
  const minSpotValue = rawSignal[minSpotIndex];
  const maxSpotValue = rawSignal[maxSpotIndex];

  const normalizedSignal = rawSignal.map((value) => (value - minSpotValue) / (maxSpotValue - minSpotValue));
  return normalizedSignal;
};

const _extractDeltaSensor = (a: number[], baselineBounds: number[], plateauBounds: number[]): number | undefined => {
  if (a.length > 0) {
    const baselineMean = mean(a.slice(...baselineBounds));
    const analyteMean = mean(a.slice(...plateauBounds));
    return analyteMean - baselineMean;
  }

  return undefined;
};

export const computeDeltaSensor = (record: RecordValueWithSensograms, kind: SENSOR_NATURE) => {
  if (record.baselineStart === undefined || record.baselineEnd === undefined) {
    throw Error('Baseline bounds are undefined');
  }
  if (record.analyteStart === undefined || record.analyteEnd === undefined) {
    throw Error('Analyte bounds are undefined');
  }

  const baselineBounds = [record.baselineStart, record.baselineEnd];
  const analyteBounds = [record.analyteStart, record.analyteEnd];

  let a: number[] = [];
  switch (kind) {
    case SENSOR_NATURE.Humidity:
      a = record.humiditySeries;
      break;
    case SENSOR_NATURE.Temperature:
      a = record.temperatureSeries;
      break;
    default:
      console.log('sensor kind unknown when computing delta sensor: ', kind);
  }

  const deltaSensor = _extractDeltaSensor(a, baselineBounds, analyteBounds);
  return deltaSensor;
};

export const correctHumidity = (a: number[], deltaHumidity: number, spotgrid: number[], humidityCalibrant: SignatureWithSpotgrid): number[] => {
  if (humidityCalibrant.deltaHumidity !== undefined) {
    let dh = humidityCalibrant.deltaHumidity;
    console.log('dh is', dh);
    let affinities = Object.fromEntries(humidityCalibrant.spotsgrid.map((key, index) => [key, humidityCalibrant.signature[index] / dh]));
    return a.map((x, idx) => x - deltaHumidity * affinities[spotgrid[idx]]);
  }

  return [];
};

export const getAggregatedSpotsgridIndicesMap = (spotsgrid1d: number[]): Record<number, number[]> => {
  let _aggregationIndicesMap: Record<number, number[]> = {};
  if (!spotsgrid1d) {
    console.log('could not compute aggregation indices : spotsgrid is empty');
    return _aggregationIndicesMap;
  }
  // Aggregate by peptide
  for (let i = 0; i < spotsgrid1d.length; i++) {
    let aggKey = spotsgrid1d[i];
    if (aggKey < 0) {
      continue;
    }
    if (_aggregationIndicesMap[aggKey] === undefined) {
      _aggregationIndicesMap[aggKey] = [];
    }
    _aggregationIndicesMap[aggKey].push(i);
  }
  return _aggregationIndicesMap;
};

export const aggregateSeriesFromIndicesMap = (series: number[][], aggregationIndicesMap: Record<number, number[]>, aggregationMethod: AggregationMethod): [number[][], number[]] => {
  let aggregatedSeries: number[][] = createEmpty2dArray(series.length);
  let aggregatedSpotsgrid1d: number[] = [];
  for (const [aggKey, aggIndices] of Object.entries(aggregationIndicesMap)) {
    for (let j = 0; j < series.length; j++) {
      aggregatedSeries[j].push(
        agg(
          aggIndices.map((i) => series[j][i]),
          aggregationMethod
        )
      );
    }
    aggregatedSpotsgrid1d.push(parseInt(aggKey));
  }
  return [aggregatedSeries, aggregatedSpotsgrid1d];
};

export const aggregateSensogramSpans = (sensogramSpans: number[][], spotsgrid1d: number[], aggregationMethod: AggregationMethod): [number[][], number[]] => {
  const _aggregationIndicesMap = getAggregatedSpotsgridIndicesMap(spotsgrid1d);
  const aggregatedSensogramSpans: number[][] = [];
  const aggregatedSpotsgrid1d: number[] = [];
  for (const [aggKey, indices] of Object.entries(_aggregationIndicesMap)) {
    let aggregatedSensogramSpan: number[] = [];
    for (let i = 0; i < sensogramSpans[0].length; i++) {
      aggregatedSensogramSpan.push(
        agg(
          indices.map((j) => sensogramSpans[j][i]),
          aggregationMethod
        )
      );
    }
    aggregatedSensogramSpans.push(aggregatedSensogramSpan);
    aggregatedSpotsgrid1d.push(parseInt(aggKey));
  }
  return [aggregatedSensogramSpans, aggregatedSpotsgrid1d];
};

export const filterSensorgramSpans = (sensorgramSpans: number[][], spotgrid1d: number[]): [number[][], number[]] => {
  let filteredSensorgram: number[][] = [];
  let filteredSpotgrid: number[] = [];
  for (let i = 0; i < sensorgramSpans.length; i++) {
    let sensorInt = spotgrid1d[i];
    if (sensorInt >= 1) {
      filteredSensorgram.push(sensorgramSpans[i]);
      filteredSpotgrid.push(sensorInt);
    }
  }
  return [filteredSensorgram, filteredSpotgrid];
};

export const softmaxForDistances = (distances: { [key: string]: number }): { [key: string]: number } => {
  // Get the distance values and negate them
  const values = Object.values(distances).map((distance) => -distance);

  // Find the maximum value for numerical stability
  const maxDistance = Math.max(...values);

  // Calculate the exponential of each element
  const expDistances = values.map((distance) => Math.exp(distance - maxDistance));

  // Calculate the sum of exponentials
  const sumExpDistances = expDistances.reduce((sum, value) => sum + value, 0);

  // Calculate the softmax probabilities and round them
  const softmaxValues = expDistances.map((expValue) => Math.round(100 * (expValue / sumExpDistances)));

  // Construct the result object with the same keys as the input
  const softmaxResult: { [key: string]: number } = {};
  Object.keys(distances).forEach((key, index) => {
    softmaxResult[key] = softmaxValues[index];
  });
  console.log('Softmax Probabilities:', softmaxResult);
  return softmaxResult;
};

// Determine if the data should be decimated. RD : and therefore update
export const shouldCompute = (timestamp: number, lastDecimationTickRef: React.MutableRefObject<number>) => {
  if (timestamp - lastDecimationTickRef.current >= 1000 / DEFAULT_PLOT_DECIMATED_FPS) {
    lastDecimationTickRef.current = timestamp;
    //console.log('true')
    return true;
  } else {
    //console.log('false')

    return false;
  }
};

export const updateKineticSeries = (
  averageMZI: number,
  correctedMZI: number[],
  averageMZISeriesRef: React.MutableRefObject<number[]>, // Pass the whole ref
  decimatedMZISeriesCorrected: React.MutableRefObject<number[][]> // Pass the whole ref
) => {
  // Update the .current values directly

  // Store the MZI average kinetics to update noise levels
  averageMZISeriesRef.current.push(averageMZI);
  if (averageMZISeriesRef.current.length > PLOT_WINDOW_SIZE) {
    averageMZISeriesRef.current.shift();
  }

  // Store the corrected MZI values for later algorithm work
  decimatedMZISeriesCorrected.current.push(correctedMZI);
  if (decimatedMZISeriesCorrected.current.length > PLOT_WINDOW_SIZE) {
    decimatedMZISeriesCorrected.current.shift();
  }
};

export const parseBiosensorsSignalMessagePayload = (message: CSMMisoMessage, fwVersion: string = '') => {
  try {
    let eventPayload = parseEventPayload(message.message.Payload);
    return parseBiosensorsSignalEvent(eventPayload.Data, fwVersion);
  } catch (e) {
    if (e instanceof Error) {
      console.error('Failed to parse biosensors event:', e.message);
    } else {
      console.error('Unknown error occurred while parsing biosensors event');
    }
    return null;
  }
};

export const parseMmiMessagePayload = (message: CSMMisoMessage, asEvent: boolean) => {
  try {
    const mmiBytes = asEvent ? parseEventPayload(message.message.Payload).Data : parseResponsePayload(message.message.Payload).Data;

    // MMI follows the same encding convention than bisosensor signal payloads
    // where each MMI value is encoded on 2-byte
    return parseBiosensorsSignalEvent(mmiBytes, '');
  } catch (e) {
    if (e instanceof Error) {
      console.error('Failed to parse MMI message:', e.message);
    } else {
      console.error('Unknown error occurred while parsing MMI message');
    }
    return null;
  }
};

export const computeEnvelope = (frame: number[], seriesLabels: number[]) => {
  let frameSum: number = 0;
  let activeSensorsCount: number = 0;
  let signalEnvelopeMin = 1e6;
  let signalEnvelopeMax = -1e6;
  for (let i = 0; i < frame.length; i++) {
    if (seriesLabels[i] < 0) continue;
    if (frame[i] < signalEnvelopeMin) {
      signalEnvelopeMin = frame[i];
    }
    if (frame[i] > signalEnvelopeMax) {
      signalEnvelopeMax = frame[i];
    }
    frameSum += frame[i];
    activeSensorsCount += 1;
  }
  let signalEnvelopeAvg = frameSum / activeSensorsCount;

  return [signalEnvelopeAvg, signalEnvelopeMin, signalEnvelopeMax];
};

export const linearFit = (values: number[]): LinearFitResult => {
  // Creating the x values (0, 1, 2, ..., n-1)
  const x: number[] = values.map((_, index) => index);

  // Calculate the means of x and y
  const meanX = x.reduce((sum, xi) => sum + xi, 0) / x.length;
  const meanY = values.reduce((sum, yi) => sum + yi, 0) / values.length;

  // Calculate the slope (a) and the intercept (b)
  let numerator = 0,
    denominator = 0;
  for (let i = 0; i < x.length; i++) {
    numerator += (x[i] - meanX) * (values[i] - meanY);
    denominator += (x[i] - meanX) ** 2;
  }

  const a = numerator / denominator;
  const b = meanY - a * meanX;

  // Calculate the R² value
  let totalSumOfSquares = 0;
  let residualSumOfSquares = 0;
  for (let i = 0; i < x.length; i++) {
    const predictedY = a * x[i] + b;
    totalSumOfSquares += (values[i] - meanY) ** 2;
    residualSumOfSquares += (values[i] - predictedY) ** 2;
  }

  const rSquared = 1 - residualSumOfSquares / totalSumOfSquares;

  return {
    a, // Slope
    b, // Intercept
    rSquared, // R² value
  };
};
